import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { AutoSizer, List } from 'react-virtualized';
import 'react-virtualized/styles.css';
import API from '../api';
import DownloadLogButton from '../components/DownloadLogButton';
import { REFRESH_INTERVAL_OPTIONS } from '../constants';
import useBrowserStorage from '../hooks/useBrowserStorage';
import { useDebounce } from '../utils';
import { formatBytes } from '../utils/networkUtils.js';

const COLORS = {
  error: '#ff6b6b',
  warn: '#ffd43b',
  stamp: '#a1a1aa',
  module: '#8bc4eb',
  level: '#e4e4e7',
};

// [\s\S] because '.' drops \r.
const RECORD_TOKENS =
  /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}(?: [+-]\d{4})?) ([A-Z]+) (\S+)( ?)([\s\S]*)$/;

const LEVEL_RANK = {
  TRACE: 10,
  DEBUG: 10,
  INFO: 20,
  WARNING: 30,
  ERROR: 40,
  CRITICAL: 40,
};

const FIRST_PARTY = new Set([
  'core',
  'dispatcharr',
  'live_proxy',
  'vod_proxy',
  'proxy',
]);

// Anything neither first-party nor a plugin is Services.
const parseSource = (source) => {
  if (source.startsWith('apps.')) {
    const module = source.split('.')[1] || source;
    // apps.plugins is the plugin system itself, not a third-party plugin.
    if (module === 'plugins') return { module: 'plugin_sys', tier: 'plugins' };
    return { module, tier: 'app' };
  }
  if (source.startsWith('plugins.'))
    return { module: source.split('.')[1] || source, tier: 'plugins' };
  // Disk-loaded plugins import as _dispatcharr_plugin_<key>.
  if (source.startsWith('_dispatcharr_plugin_')) {
    const head = source.split('.')[0];
    return { module: head.slice(20) || head, tier: 'plugins' };
  }
  // First-party DB pool code logs under a django namespace.
  if (source.startsWith('django.geventpool'))
    return { module: 'geventpool', tier: 'app' };
  const head = source.split('.')[0] || source;
  return { module: head, tier: FIRST_PARTY.has(head) ? 'app' : 'services' };
};

const STAMP_OFFSET = /^(.*) ([+-])(\d{2})(\d{2})$/;

const displayStamp = (stamp) => {
  const m = STAMP_OFFSET.exec(stamp);
  if (!m) return stamp;
  const minutes = m[4] === '00' ? '' : `:${m[4]}`;
  return `${m[1]} [UTC${m[2]}${Number(m[3])}${minutes}]`;
};

const parseRecord = (line) => {
  const m = RECORD_TOKENS.exec(line);
  if (!m) return null;
  const { module, tier } = parseSource(m[3]);
  return {
    stamp: displayStamp(m[1]),
    level: m[2],
    source: m[3],
    module,
    tier,
    sep: m[4],
    message: m[5],
  };
};

const levelLabel = (level) => {
  if (level === 'CRITICAL') return 'ERROR';
  if (level === 'WARNING') return 'WARN';
  if (level === 'TRACE') return 'DEBUG';
  return level;
};

const severityColor = (level) => {
  if (level === 'ERROR' || level === 'CRITICAL') return COLORS.error;
  if (level === 'WARNING') return COLORS.warn;
  return null;
};

const levelColor = (level) => {
  if (level === 'DEBUG' || level === 'TRACE') return COLORS.stamp;
  return severityColor(level) || COLORS.level;
};

// The bar marks what rises above the floor, not everything severe.
const barColor = (level, minRank) => {
  const color = severityColor(level);
  if (!color) return 'transparent';
  return LEVEL_RANK[level] > minRank ? color : 'transparent';
};

const RECORD_START =
  /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}(?: [+-]\d{4})? /;

const SEARCH_DEBOUNCE_MS = 200;

// Bytes hold one full response from the API; lines bound the entry overhead.
const MAX_BUFFER_BYTES = 24 * 1024 * 1024;
const MAX_BUFFER_LINES = 300000;
const EMPTY_BUFFER = { entries: [], bytes: 0, truncated: false };

// How close to the live edge still counts as watching it.
const FOLLOW_SLACK_PX = 50;

const LEVEL_COL_MAX = 12;
const MODULE_COL_MAX = 12;

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All categories' },
  { value: 'app', label: 'App' },
  { value: 'plugins', label: 'Plugins' },
  { value: 'services', label: 'Services' },
];

const LEVEL_OPTIONS = [
  { value: '10', label: 'Debug' },
  { value: '20', label: 'Info' },
  { value: '30', label: 'Warning' },
  { value: '40', label: 'Error' },
];

// Mirrors the collector's continuation rules.
const TRACEBACK_HEAD = 'Traceback';

// Ids survive the head slice and the reversal, so React inserts only new rows.
let nextEntryId = 0;

// Returns the traceback state so the next chunk can resume inside a split one.
const classifyLines = (lines, inTraceback = false) => {
  const entries = [];
  for (const line of lines) {
    const record = parseRecord(line);
    let kind = 'standalone';
    if (record) {
      inTraceback = false;
      kind = 'record';
    } else if (RECORD_START.test(line)) {
      inTraceback = false;
    } else if (line.startsWith(TRACEBACK_HEAD)) {
      inTraceback = true;
      kind = 'continuation';
    } else if (inTraceback || /^[ \t]/.test(line) || line === '') {
      kind = 'continuation';
    }
    entries.push({ id: nextEntryId++, line, record, kind });
  }
  return { entries, inTraceback };
};

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A record and the continuations trailing it are kept or dropped together.
const filterEntries = (entries, minRank, category, matcher) => {
  const out = [];
  let start = 0;
  while (start < entries.length) {
    let end = start + 1;
    while (end < entries.length && entries[end].kind === 'continuation')
      end += 1;
    const record = entries[start].record;
    let keep = true;
    if (record) {
      const rank = LEVEL_RANK[record.level];
      if (rank !== undefined && rank < minRank) keep = false;
      else if (category !== null && record.tier !== category) keep = false;
    }
    if (keep && matcher) {
      keep = false;
      for (let i = start; i < end && !keep; i += 1) {
        keep = matcher.test(entries[i].line);
      }
    }
    if (keep) for (let i = start; i < end; i += 1) out.push(entries[i]);
    start = end;
  }
  return out;
};

// whiteSpace and textIndent inherit from the hanging block; reset them here.
const columnStyle = (width) => ({
  display: 'inline-block',
  width: `${width}ch`,
  whiteSpace: 'nowrap',
  textIndent: '0px',
  verticalAlign: 'bottom',
});

// One table per column layout and floor, so a few objects cover every row.
const buildStyles = (stampW, levelW, moduleW, indent, minLevel) => {
  const cache = new Map();
  return {
    indent,
    stamp: { ...columnStyle(stampW), color: COLORS.stamp },
    module: {
      ...columnStyle(moduleW),
      color: COLORS.module,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    plain: {
      borderLeft: '2px solid transparent',
      paddingLeft: 8,
      color: COLORS.stamp,
    },
    // Built on first sight of a level, so an unexpected one still renders.
    forLevel: (level) => {
      let style = cache.get(level);
      if (!style) {
        const severity = severityColor(level);
        style = {
          line: {
            borderLeft: `2px solid ${barColor(level, minLevel)}`,
            paddingLeft: 8,
          },
          // Continuations start under the message column.
          continuation: {
            borderLeft: `2px solid ${barColor(level, minLevel)}`,
            paddingLeft: `calc(8px + ${indent}ch)`,
            color: severity || undefined,
          },
          level: {
            ...columnStyle(levelW),
            color: levelColor(level),
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          message: severity ? { color: severity } : undefined,
        };
        cache.set(level, style);
      }
      return style;
    },
  };
};

const byteLength = (entries) => {
  let bytes = 0;
  for (const entry of entries) bytes += entry.line.length + 1;
  return bytes;
};

// Drops from the head until both bounds hold, paying only for what it drops.
const trimBuffer = (entries, bytes, truncated) => {
  let drop = 0;
  while (
    drop < entries.length &&
    (bytes > MAX_BUFFER_BYTES || entries.length - drop > MAX_BUFFER_LINES)
  ) {
    bytes -= entries[drop].line.length + 1;
    drop += 1;
  }
  return drop
    ? { entries: entries.slice(drop), bytes, truncated: true }
    : { entries, bytes, truncated };
};

const emptyState = (message) => (
  <Text size="sm" c="dimmed" ta="center" py="md">
    {message}
  </Text>
);

const buildBlocks = (entries) => {
  const blocks = [];
  for (const entry of entries) {
    const last = blocks[blocks.length - 1];
    if (entry.record) {
      blocks.push({ id: entry.id, record: entry.record, continuations: [] });
    } else if (entry.kind === 'continuation' && last) {
      if (last.record) last.continuations.push(entry.line);
      else last.lines.push(entry.line);
    } else {
      blocks.push({ id: entry.id, lines: [entry.line] });
    }
  }
  return blocks;
};

// One row per line; a continuation keeps its record's level for colour.
const flattenBlocks = (blocks) => {
  const rows = [];
  for (const block of blocks) {
    if (!block.record) {
      block.lines.forEach((text, i) =>
        rows.push({ id: `${block.id}.${i}`, text })
      );
      continue;
    }
    rows.push({ id: `${block.id}`, record: block.record });
    block.continuations.forEach((text, i) =>
      rows.push({ id: `${block.id}.${i}`, text, level: block.record.level })
    );
  }
  return rows;
};

// One visual line per row.
const ROW_HEIGHT = 18;

// Set on the list container; every row inherits the metrics.
const BODY_FONT = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
  lineHeight: `${ROW_HEIGHT}px`,
  // The scroller owns the gutter, so the scrollbar stays at the panel edge.
  paddingInline: 'var(--mantine-spacing-sm)',
};

const CLIPPED = {
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'pre',
};

// anywhere breaks unbroken tokens like URLs and SQL dumps.
const WRAPPED = {
  flex: '1 1 auto',
  minWidth: 0,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const ExpandToggle = ({ expanded, hidden, onClick }) => (
  <Button
    size="compact-xs"
    variant="default"
    h={16}
    px={6}
    mt={1}
    ml={6}
    fz={10}
    ff="text"
    style={{
      flex: 'none',
      '--button-color': COLORS.stamp,
      '--button-hover-color': COLORS.level,
    }}
    leftSection={expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
    aria-expanded={expanded}
    onClick={onClick}
  >
    <span style={{ textBox: 'trim-both cap alphabetic' }}>
      +{hidden.toLocaleString()} chars
    </span>
  </Button>
);

const LogRow = React.memo(
  ({ row, styles, width, expanded, opened, onToggle, onMeasure }) => {
    const frameRef = useRef(null);
    const textRef = useRef(null);
    const [hidden, setHidden] = useState(0);
    const cut = hidden > 0;
    // Collapsed, a row reads widths only.
    useLayoutEffect(() => {
      if (expanded) {
        onMeasure(row.id, frameRef.current.offsetHeight);
        return;
      }
      const el = textRef.current;
      const over = el.scrollWidth - el.clientWidth;
      setHidden(
        over > 1
          ? Math.ceil((over * el.textContent.length) / el.scrollWidth)
          : 0
      );
    }, [row, width, expanded, cut, onMeasure]);

    const level = row.record ? row.record.level : row.level;
    const tone = level ? styles.forLevel(level) : null;
    const frame = !tone
      ? styles.plain
      : row.record
        ? tone.line
        : tone.continuation;
    const text = !expanded
      ? CLIPPED
      : row.record
        ? {
            ...WRAPPED,
            paddingLeft: `${styles.indent}ch`,
            textIndent: `-${styles.indent}ch`,
          }
        : WRAPPED;
    return (
      <div
        ref={frameRef}
        style={{
          ...frame,
          display: 'flex',
          alignItems: 'flex-start',
          height: expanded ? 'auto' : ROW_HEIGHT,
        }}
      >
        <span ref={textRef} style={text}>
          {row.record ? (
            <>
              <span style={styles.stamp}>{row.record.stamp}</span>{' '}
              <span style={tone.level} title={row.record.level}>
                {levelLabel(row.record.level)}
              </span>{' '}
              <span style={styles.module} title={row.record.source}>
                {row.record.module}
              </span>
              {row.record.sep}
              <span style={tone.message}>{row.record.message}</span>
            </>
          ) : (
            row.text
          )}
        </span>
        {(cut || expanded) && (
          <ExpandToggle
            expanded={expanded}
            hidden={expanded ? opened : hidden}
            onClick={() => onToggle(row.id, hidden)}
          />
        )}
      </div>
    );
  }
);

const LogBody = React.memo(
  ({
    name,
    blocks,
    styles,
    loading,
    empty,
    loadError,
    onRetry,
    newestFirst,
  }) => {
    const listRef = useRef(null);
    const rows = useMemo(() => flattenBlocks(blocks), [blocks]);
    // Expanded row id -> its hidden count; only these rows are ever taller.
    const [expanded, setExpanded] = useState(() => new Map());
    const heightsRef = useRef(new Map());
    // Follow the live edge until the reader scrolls away or toggles a row.
    const [following, setFollowing] = useState(true);
    const followingRef = useRef(true);
    // The grid opens at the top and reports it, which is not the reader moving.
    const reachedRef = useRef(false);

    // A flipped order pins the other end, which has to be reached in turn.
    useEffect(() => {
      reachedRef.current = false;
    }, [newestFirst, name]);

    useEffect(() => {
      setExpanded(new Map());
      heightsRef.current.clear();
    }, [name]);

    // Summed from rowHeight, not the DOM.
    const resize = useCallback(() => {
      listRef.current?.recomputeRowHeights();
      listRef.current?.measureAllRows();
    }, []);
    const resizedRef = useRef(false);
    useEffect(() => {
      // Nothing expanded now or before: every row is ROW_HEIGHT already.
      if (!expanded.size && !resizedRef.current) return;
      resizedRef.current = expanded.size > 0;
      resize();
    }, [expanded, rows, resize]);

    const toggle = useCallback((id, hidden) => {
      followingRef.current = false;
      setFollowing(false);
      setExpanded((prev) => {
        const next = new Map(prev);
        if (!next.delete(id)) next.set(id, hidden);
        return next;
      });
    }, []);

    const measure = useCallback(
      (id, height) => {
        if (heightsRef.current.get(id) === height) return;
        heightsRef.current.set(id, height);
        resize();
      },
      [resize]
    );

    const rowHeight = useCallback(
      ({ index }) => {
        const id = rows[index]?.id;
        return (expanded.has(id) && heightsRef.current.get(id)) || ROW_HEIGHT;
      },
      [rows, expanded]
    );

    // Newest sits at whichever end the order puts it.
    const onScroll = useCallback(
      ({ clientHeight, scrollHeight, scrollTop }) => {
        // A list shorter than its viewport cannot say where the reader is.
        if (scrollHeight <= clientHeight) return;
        const next = newestFirst
          ? scrollTop <= FOLLOW_SLACK_PX
          : scrollHeight - scrollTop - clientHeight <= FOLLOW_SLACK_PX;
        // Until the pin has landed once, a report is the list catching up.
        if (!reachedRef.current) {
          reachedRef.current = next;
          return;
        }
        // A ref first: scrolling fires far more often than the answer changes.
        if (next === followingRef.current) return;
        followingRef.current = next;
        setFollowing(next);
      },
      [newestFirst]
    );

    const pinned =
      following && rows.length
        ? newestFirst
          ? 0
          : rows.length - 1
        : undefined;

    if (loading && empty) return <Loader ml="sm" />;
    if (empty && loadError) {
      return (
        <Group gap="sm" px="sm">
          <Text size="sm" c="red">
            Failed to load {name}
          </Text>
          <Button size="xs" variant="subtle" onClick={() => onRetry()}>
            Retry
          </Button>
        </Group>
      );
    }
    if (empty) return emptyState('(empty)');
    if (!rows.length) return emptyState('(no records match the filters)');

    return (
      <AutoSizer>
        {({ height, width }) => (
          <List
            className="log-body"
            // Every collapsed row is exactly this height.
            estimatedRowSize={ROW_HEIGHT}
            height={height}
            onScroll={onScroll}
            overscanRowCount={12}
            ref={listRef}
            rowCount={rows.length}
            rowHeight={rowHeight}
            rowRenderer={({ index, key, style }) => (
              <div key={key} style={style}>
                <LogRow
                  key={rows[index].id}
                  row={rows[index]}
                  styles={styles}
                  width={width}
                  expanded={expanded.has(rows[index].id)}
                  opened={expanded.get(rows[index].id)}
                  onToggle={toggle}
                  onMeasure={measure}
                />
              </div>
            )}
            scrollToAlignment={newestFirst ? 'start' : 'end'}
            scrollToIndex={pinned}
            style={BODY_FONT}
            width={width}
          />
        )}
      </AutoSizer>
    );
  }
);

const LogFileViewPage = () => {
  const { name } = useParams();
  // The byte total rides with the entries so trimming never rescans them.
  const [buffer, setBuffer] = useState(EMPTY_BUFFER);
  const entries = buffer.entries;
  const [loading, setLoading] = useState(true);
  const [refreshSetting, setRefreshSetting] = useBrowserStorage(
    'log-viewer-refresh-interval',
    0
  );
  // A stored value outside the options would render an empty select.
  const refreshSeconds = REFRESH_INTERVAL_OPTIONS.some(
    (option) => option.value === String(refreshSetting)
  )
    ? refreshSetting
    : 0;
  const [paused, setPaused] = useState(false);
  const [newestFirst, setNewestFirst] = useBrowserStorage(
    'log-viewer-newest-first',
    false
  );
  const [minLevelSetting, setMinLevelSetting] = useBrowserStorage(
    'log-viewer-min-level',
    20
  );
  const minLevel = LEVEL_OPTIONS.some(
    (option) => option.value === String(minLevelSetting)
  )
    ? Number(minLevelSetting)
    : 20;
  const [categorySetting, setCategorySetting] = useBrowserStorage(
    'log-viewer-category',
    'all'
  );
  const category =
    categorySetting !== 'all' &&
    CATEGORY_OPTIONS.some((option) => option.value === categorySetting)
      ? categorySetting
      : null;
  const [search, setSearch] = useState('');
  // The box keeps up with typing; the filter waits for a pause.
  const query = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const matcher = useMemo(
    () => (query ? new RegExp(escapeRegExp(query), 'i') : null),
    [query]
  );
  const [loadError, setLoadError] = useState(false);

  const loadingRef = useRef(false);
  const failuresRef = useRef(0);
  const cursorRef = useRef(null);
  // Overlapping loads carry the same cursor; only the newest may land.
  const requestRef = useRef(0);
  // classifyLines resumes from here so a split traceback keeps its tail.
  const tracebackRef = useRef(false);

  // Widths come from every record; the filtered set would shift on each keystroke.
  const cols = useMemo(() => {
    let stamp = 0;
    let level = 0;
    let module = 0;
    for (const entry of entries) {
      if (!entry.record) continue;
      stamp = Math.max(stamp, entry.record.stamp.length);
      level = Math.max(level, levelLabel(entry.record.level).length);
      module = Math.max(module, entry.record.module.length);
    }
    return {
      stamp,
      // Floored at ERROR's width so the column stays put while tailing.
      level: Math.min(Math.max(level, 5), LEVEL_COL_MAX),
      module: Math.min(module, MODULE_COL_MAX),
    };
  }, [entries]);

  const blocks = useMemo(() => {
    const kept = filterEntries(entries, minLevel, category, matcher);
    // Reversed as blocks, not entries, so continuations stay with their record.
    const built = buildBlocks(kept);
    if (newestFirst) built.reverse();
    return built;
  }, [entries, newestFirst, minLevel, category, matcher]);

  // Wrapped lines and continuations hang under the message column.
  const messageIndent = cols.stamp + cols.level + cols.module + 3;

  // Keyed off the primitives so the style identities survive a poll.
  const styles = useMemo(
    () =>
      buildStyles(cols.stamp, cols.level, cols.module, messageIndent, minLevel),
    [cols.stamp, cols.level, cols.module, messageIndent, minLevel]
  );

  // What is held, not what either cap says: both ends can do the trimming.
  const notice = buffer.truncated
    ? `Showing the last ${formatBytes(buffer.bytes)} of the log`
    : null;

  // A reset replaces the buffer; a delta is classified alone and appended.
  const applyResponse = useCallback((response) => {
    cursorRef.current = response.cursor || null;
    const lines = response.content ? response.content.split('\n') : [];
    // The body ends on a newline, so the split leaves a trailing empty line.
    if (lines[lines.length - 1] === '') lines.pop();
    if (response.reset !== false) {
      const { entries: next, inTraceback } = classifyLines(lines);
      tracebackRef.current = inTraceback;
      setBuffer(trimBuffer(next, byteLength(next), response.truncated));
      return;
    }
    if (!lines.length) return;
    const { entries: added, inTraceback } = classifyLines(
      lines,
      tracebackRef.current
    );
    tracebackRef.current = inTraceback;
    setBuffer((prev) =>
      trimBuffer(
        prev.entries.concat(added),
        prev.bytes + byteLength(added),
        prev.truncated
      )
    );
  }, []);

  const load = useCallback(
    async (showLoading = true, { silent = false } = {}) => {
      loadingRef.current = true;
      const version = (requestRef.current += 1);
      if (showLoading) setLoading(true);
      try {
        const response = await API.getLogFile(name, {
          silent,
          cursor: cursorRef.current,
        });
        // Superseded by a newer load that already carried this delta.
        if (version !== requestRef.current) return true;
        // Silent polls resolve to undefined on failure.
        if (!response) return false;
        applyResponse(response);
        if (!silent) setLoadError(false);
        return true;
      } catch {
        // getLogFile has already toasted.
        if (!silent) setLoadError(true);
        return false;
      } finally {
        loadingRef.current = false;
        if (showLoading) setLoading(false);
      }
    },
    [name, applyResponse]
  );

  useEffect(() => {
    // Offsets belong to one file, and a stale in-flight response must not land here.
    requestRef.current += 1;
    cursorRef.current = null;
    tracebackRef.current = false;
    setBuffer(EMPTY_BUFFER);
    setLoadError(false);
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshSeconds || paused) return undefined;
    failuresRef.current = 0;
    const id = setInterval(async () => {
      if (document.hidden || loadingRef.current) return;
      const ok = await load(false, { silent: true });
      if (ok) {
        failuresRef.current = 0;
      } else {
        failuresRef.current += 1;
        if (failuresRef.current >= 3) {
          setPaused(true);
          notifications.show({
            title: 'Auto-refresh paused',
            message:
              'Paused auto-refresh after repeated errors loading the log.',
            color: 'yellow',
            autoClose: 6000,
          });
        }
      }
    }, refreshSeconds * 1000);
    return () => clearInterval(id);
  }, [refreshSeconds, paused, load]);

  return (
    <Box p="md">
      <Paper
        withBorder
        radius="md"
        p={0}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - var(--mantine-spacing-md) * 2)',
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            flex: '0 0 auto',
            borderBottom: '1px solid var(--mantine-color-default-border)',
            padding: 'var(--mantine-spacing-sm)',
          }}
        >
          <Group justify="space-between">
            <Group gap="sm">
              <Anchor component={Link} to="/logs" size="sm">
                ← Logs
              </Anchor>
              <Title order={4}>{name}</Title>
            </Group>
            <Group gap="sm">
              {notice && (
                <Text size="sm" c="yellow">
                  {notice}
                </Text>
              )}
              <TextInput
                size="xs"
                label="Search"
                placeholder="Filter text"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                style={{ width: 240 }}
              />
              <Select
                size="xs"
                label="Level"
                value={String(minLevel)}
                onChange={(value) => setMinLevelSetting(parseInt(value))}
                allowDeselect={false}
                data={LEVEL_OPTIONS}
                style={{ width: 110 }}
              />
              <Select
                size="xs"
                label="Category"
                value={category === null ? 'all' : category}
                onChange={(value) => setCategorySetting(value)}
                allowDeselect={false}
                data={CATEGORY_OPTIONS}
                style={{ width: 130 }}
              />
              <Select
                size="xs"
                label="Order"
                value={newestFirst ? 'newest' : 'oldest'}
                onChange={(value) => setNewestFirst(value === 'newest')}
                allowDeselect={false}
                data={[
                  { value: 'newest', label: 'Newest first' },
                  { value: 'oldest', label: 'Newest last' },
                ]}
                style={{ width: 130 }}
              />
              <Select
                size="xs"
                label="Auto Refresh"
                value={paused ? '0' : refreshSeconds.toString()}
                onChange={(value) => {
                  setPaused(false);
                  setRefreshSetting(parseInt(value));
                }}
                allowDeselect={false}
                data={REFRESH_INTERVAL_OPTIONS}
                style={{ width: 120 }}
              />
              <Button
                size="xs"
                variant="subtle"
                onClick={() => load()}
                loading={loading}
                style={{ marginTop: 'auto' }}
              >
                Refresh
              </Button>
              <DownloadLogButton name={name} style={{ marginTop: 'auto' }} />
            </Group>
          </Group>
        </Box>

        <Box
          // Without minHeight:0 a flex item will not shrink below its content.
          style={{ flex: '1 1 auto', overflow: 'hidden', minHeight: '0px' }}
        >
          <LogBody
            name={name}
            blocks={blocks}
            styles={styles}
            loading={loading}
            empty={!entries.length}
            loadError={loadError}
            onRetry={load}
            newestFirst={newestFirst}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default LogFileViewPage;
